import { useState } from 'react'
import { Loader2, Package } from 'lucide-react'
import { useProduct } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

function imageUrl(image: {
  hidden?: boolean
  public_url?: string | null
  store_url?: string | null
}) {
  if (image.hidden) return null
  return image.public_url || image.store_url || null
}

function attributeValue(value: {
  number_value?: string | null
  text_value?: string | null
  bool_value?: boolean | null
  range_min?: string | null
  range_max?: string | null
  unit?: string | null
  enum_value?: string | null
}) {
  const unit = value.unit ? ` ${value.unit}` : ''
  if (value.text_value) return value.text_value
  if (value.enum_value) return value.enum_value
  if (value.number_value) return `${value.number_value}${unit}`
  if (value.range_min || value.range_max) {
    return `${value.range_min || '—'}–${value.range_max || '—'}${unit}`
  }
  if (value.bool_value != null) return value.bool_value ? 'Yes' : 'No'
  return '—'
}

function dimensions(
  width?: string | null,
  depth?: string | null,
  height?: string | null,
) {
  const values = [width, depth, height].map((value) =>
    value && Number(value) > 0 ? value : null,
  )
  return values.some(Boolean) ? values.map((value) => value || '—').join(' × ') + ' in' : '—'
}

function weight(value?: string | null) {
  return value && Number(value) > 0 ? `${value} lb` : '—'
}

export function ProductDetailsDialogTrigger({
  productUuid,
  model,
  description,
}: {
  productUuid: string
  model: string
  description: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        className="block max-w-[20rem] truncate text-left underline-offset-2 hover:text-foreground hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        title={description}
        aria-label={`View full product details for ${model}`}
        onClick={() => setOpen(true)}
      >
        {description}
      </button>
      {open ? (
        <ProductDetailsDialogContent
          productUuid={productUuid}
          model={model}
          description={description}
        />
      ) : null}
    </Dialog>
  )
}

function ProductDetailsDialogContent({
  productUuid,
  model,
  description,
}: {
  productUuid: string
  model: string
  description: string
}) {
  const productQuery = useProduct(productUuid)
  const product = productQuery.data
  const visibleImages = product?.images
    .map((image) => ({ ...image, url: imageUrl(image) }))
    .filter((image): image is typeof image & { url: string } => Boolean(image.url)) ?? []
  const fullDescription =
    product?.current_description?.content_text ||
    product?.seo_description ||
    product?.specifications ||
    description

  return (
    <DialogContent className="max-h-[88dvh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle>{product?.model || model}</DialogTitle>
            {product?.configurable ? <Badge variant="outline">Configurable</Badge> : null}
          </div>
          <DialogDescription>
            {product?.manufacturer_name || 'Catalog product'}
          </DialogDescription>
        </DialogHeader>

        {productQuery.isLoading ? (
          <div className="flex min-h-72 items-center justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label="Loading product details" />
          </div>
        ) : productQuery.error || !product ? (
          <p className="rounded-lg border border-destructive/30 p-4 text-sm text-destructive">
            Product details could not be loaded.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
            <div className="space-y-2">
              {visibleImages[0] ? (
                <>
                  <img
                    src={visibleImages[0].url}
                    alt={product.model}
                    className="aspect-square w-full rounded-lg border bg-muted object-contain p-2"
                  />
                  {visibleImages.length > 1 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {visibleImages.slice(1, 4).map((image) => (
                        <img
                          key={image.uuid}
                          src={image.url}
                          alt=""
                          className="aspect-square w-full rounded-md border bg-muted object-contain p-1"
                        />
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex aspect-square items-center justify-center rounded-lg border bg-muted">
                  <Package className="size-10 text-muted-foreground/40" aria-hidden="true" />
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-5">
              <section>
                <h3 className="text-sm font-semibold">Description</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                  {fullDescription}
                </p>
              </section>

              <dl className="grid gap-3 rounded-lg border bg-muted/20 p-4 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">Product dimensions</dt>
                  <dd className="mt-1">{dimensions(product.width, product.depth, product.height)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">Shipping dimensions</dt>
                  <dd className="mt-1">
                    {dimensions(product.shipping_width, product.shipping_depth, product.shipping_height)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">Product weight</dt>
                  <dd className="mt-1">{weight(product.weight)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">Shipping weight</dt>
                  <dd className="mt-1">{weight(product.shipping_weight)}</dd>
                </div>
              </dl>

              {product.attribute_values.length ? (
                <section>
                  <h3 className="text-sm font-semibold">AI specification facets</h3>
                  <dl className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                    {product.attribute_values.map((value) => (
                      <div key={value.uuid} className="border-b pb-2">
                        <dt className="text-xs text-muted-foreground">
                          {value.attribute_definition.group.name} · {value.attribute_definition.name}
                        </dt>
                        <dd className="mt-0.5 text-sm font-medium">{attributeValue(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : null}
            </div>
          </div>
        )}
    </DialogContent>
  )
}
